import { ReactNode, InputHTMLAttributes, forwardRef } from 'react';
import { LucideIcon, Monitor, Tablet, Smartphone } from 'lucide-react';

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'value' | 'defaultValue'> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Opcjonalna ikona po lewej (np. Monitor/Tablet/Smartphone). */
  icon?: LucideIcon;
  /** Opcjonalny label między ikoną a suwakiem. */
  label?: ReactNode;
  /** Jednostka wyświetlana obok wartości (px, %, ms). Default: bez jednostki. */
  unit?: string;
  /** Pokaż wartość po prawej. Default: true. */
  showValue?: boolean;
  className?: string;
}

/**
 * Numeric slider z opcjonalną ikoną, labelem i wartością po prawej.
 * Native <input type="range"> z `accent-accent-primary` - zachowuje
 * accessibility i keyboard control. Wzorzec używany w Kreator
 * (Desktop/Tablet/Mobile breakpoint sliders) — 15+ instancji w kodzie.
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(function Slider(
  {
    value, onChange, min = 0, max = 100, step = 1,
    icon: Icon, label, unit, showValue = true,
    disabled, className, ...rest
  },
  ref,
) {
  return (
    <div className={['flex items-center gap-3', className].filter(Boolean).join(' ')}>
      {Icon && (
        <Icon className="w-4 h-4 text-text-muted shrink-0" aria-hidden="true" />
      )}
      {label && (
        <span className="text-xs text-text-secondary font-medium shrink-0 min-w-[60px]">{label}</span>
      )}
      <input
        ref={ref}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="flex-1 h-1.5 bg-surface-hover rounded-full appearance-none cursor-pointer accent-accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
        {...rest}
      />
      {showValue && (
        <span className="shrink-0 font-mono text-xs text-accent-primary font-medium min-w-[50px] text-right">
          {value}{unit}
        </span>
      )}
    </div>
  );
});

export interface SliderCardProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Tytuł nad suwakiem (lewa strona). */
  label: ReactNode;
  /** Jednostka obok wartości (np. 'px', '%'). */
  unit?: string;
  /** Custom formatter dla wartości po prawej (np. 'Lewa: 60% / Prawa: 40%'). Override `unit`. */
  formatValue?: (value: number) => ReactNode;
  /** Opis pod suwakiem. */
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * Stacked slider w karcie z labelem na górze i wartością po prawej, suwakiem
 * full-width pod spodem, opcjonalnym opisem na dole. Wzorzec używany w
 * settings Kreatora (Proporcja kolumn, Odstęp px, Opacity itp.) — zamiast
 * inline pattern który multiplikował się 15+ razy.
 */
export function SliderCard({
  value, onChange, min = 0, max = 100, step = 1,
  label, unit, formatValue, description,
  disabled, className,
}: SliderCardProps) {
  const valueDisplay = formatValue ? formatValue(value) : `${value}${unit ?? ''}`;
  return (
    <div className={['p-4 rounded-lg border border-border-subtle bg-surface-raised', className].filter(Boolean).join(' ')}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <span className="text-sm font-medium text-text-primary">{label}</span>
        <span className="text-xs font-mono text-accent-primary font-medium shrink-0">{valueDisplay}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="w-full h-1.5 bg-surface-hover rounded-full appearance-none cursor-pointer accent-accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {description && (
        <p className="text-xs text-text-muted mt-2">{description}</p>
      )}
    </div>
  );
}

export interface BreakpointSliderValue {
  desktop: number;
  tablet: number;
  mobile: number;
}

export interface BreakpointSliderProps {
  value: BreakpointSliderValue;
  onChange: (value: BreakpointSliderValue) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
}

/**
 * Trzy suwaki Desktop/Tablet/Mobile stacked w kolumnie. Wzorzec powtarzający
 * się w Kreator (responsive spacing settings).
 */
export function BreakpointSlider({
  value, onChange, min, max, step, unit = 'px',
  label, description, className,
}: BreakpointSliderProps) {
  return (
    <div className={className}>
      {(label || description) && (
        <div className="mb-2">
          {label && <div className="text-sm font-medium text-text-secondary">{label}</div>}
          {description && <div className="mt-0.5 text-xs text-text-muted">{description}</div>}
        </div>
      )}
      <div className="space-y-2.5">
        <Slider icon={Monitor}    value={value.desktop} onChange={(v) => onChange({ ...value, desktop: v })} min={min} max={max} step={step} unit={unit} />
        <Slider icon={Tablet}     value={value.tablet}  onChange={(v) => onChange({ ...value, tablet: v })}  min={min} max={max} step={step} unit={unit} />
        <Slider icon={Smartphone} value={value.mobile}  onChange={(v) => onChange({ ...value, mobile: v })}  min={min} max={max} step={step} unit={unit} />
      </div>
    </div>
  );
}
